/**
 * V2 Button Style Groups — Factory for Accordion-based button style subsections
 *
 * Creates ButtonStyleGroup[] from existing atomic field factories.
 * Each group becomes a collapsible Accordion in the button style panel.
 *
 * Groups: Container, Gradient, Shadow, Effects, Icon
 *
 * Gradient, Shadow, Effects, Icon all use customComponent pattern:
 * single enable/disable checkbox with fields shown below when enabled.
 */

import type { ButtonStyleGroup } from '../types/editorFieldTypes';
import {
  createButtonContainerStyleFields,
  createV2ButtonAppearanceFields,
  createV2IconFields,
  remapButtonStyleFieldsForV2,
} from './atomicFields';
import { createGradientEditorComponent } from '../components/shared/styleFields/gradientEditorFactory';
import {
  createShadowEditorComponent,
  createEffectsEditorComponent,
  createIconEditorComponent,
} from '../components/shared/styleFields/styleGroupEditorFactories';

/**
 * Create V2 button style groups for Accordion-based rendering.
 *
 * @param basePath - TapTarget base path (e.g., 'content.actionBar.primary')
 * @param options.includeOnCancelAction - Include onCancelAction field in Container group (purchase buttons only)
 */
export function createV2ButtonStyleGroups(
  basePath: string,
  options?: { includeOnCancelAction?: boolean },
): ButtonStyleGroup[] {
  // Container: shared V1/V2 fields (bg, border, dimensions, fullWidth)
  const containerFields = remapButtonStyleFieldsForV2(
    createButtonContainerStyleFields(basePath),
  );

  if (options?.includeOnCancelAction) {
    containerFields.push({
      path: `${basePath}.action.onCancelAction`,
      label: 'downsale on cancel',
      type: 'select',
      options: [{ value: 'enabled', label: 'Navigate to Downsale' }],
      defaultValue: 'enabled',
      isNullable: true,
    });
  }

  // V2-only appearance fields, remapped — kept for getButtonStyleValues compatibility
  const allAppearanceFields = remapButtonStyleFieldsForV2(
    createV2ButtonAppearanceFields(basePath),
  );

  // Split by semantic group (fields still needed for getButtonStyleValues extraction)
  const gradientFields = allAppearanceFields.filter((f) =>
    f.label.startsWith('gradient'),
  );
  const shadowFields = allAppearanceFields.filter((f) =>
    f.label.startsWith('shadow'),
  );
  const effectFields = allAppearanceFields.filter(
    (f) => f.label === 'opacity' || f.label === 'animation'
      || f.label === 'enterAnimation' || f.label === 'tapAnimation' || f.label === 'idleAnimation',
  );

  // Icon fields — kept for getButtonStyleValues extraction
  const iconFields = createV2IconFields(basePath);

  return [
    {
      id: 'container',
      title: 'Container',
      defaultExpanded: true,
      fields: containerFields,
      paddingConfig: {
        basePath: `${basePath}.layout.insets`,
        label: 'Button Padding',
      },
    },
    {
      id: 'gradient',
      title: 'Gradient',
      defaultExpanded: false,
      fields: gradientFields,
      objectPath: `${basePath}.appearance.gradient`,
      customComponent: createGradientEditorComponent(basePath),
    },
    {
      id: 'shadow',
      title: 'Shadow',
      defaultExpanded: false,
      fields: shadowFields,
      objectPath: `${basePath}.appearance.shadow`,
      customComponent: createShadowEditorComponent(basePath),
    },
    {
      id: 'effects',
      title: 'Effects',
      defaultExpanded: false,
      fields: effectFields,
      customComponent: createEffectsEditorComponent(basePath),
    },
    {
      id: 'icon',
      title: 'Icon',
      defaultExpanded: false,
      fields: iconFields,
      objectPath: `${basePath}.icon`,
      customComponent: createIconEditorComponent(basePath),
      paddingConfig: {
        basePath: `${basePath}.iconInsets`,
        label: 'Icon Padding',
      },
    },
  ];
}
